import {
  IBrand,
  PracticeSetupStepCategory,
  PracticeSetupStepId,
} from '@principle-theorem/principle-core/interfaces';
import { Firestore, undeletedFilter } from '@principle-theorem/shared';
import { from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { Brand } from '../../brand';
import { PracticeSetupStepValidation } from './practice-setup-step-validation';
import {
  IPageVisitTrackingConfig,
  IPracticeSetupStepDefinition,
  IValidationResult,
} from './practice-setup-step.types';
import { initRawSchema, toParagraphContent } from '@principle-theorem/editor';

const pageVisitTracking: IPageVisitTrackingConfig = {
  routes: ['settings/brand/{brandId}/snippets'],
  validationMode: 'all',
};

export const snippetsStep: IPracticeSetupStepDefinition = {
  id: PracticeSetupStepId.Snippets,
  category: PracticeSetupStepCategory.AdditionalSettings,
  title: 'Create Shared Snippets',
  description: initRawSchema([
    toParagraphContent(
      `Snippets are pre-written blocks of text that can be inserted anywhere in Principle that accepts written input, including clinical notes, tasks, and emails. Rather than typing the same phrases repeatedly, your team can insert a snippet instantly by typing # followed by the snippet's keyword.`
    ),
    toParagraphContent(
      `For busy clinical environments, snippets can significantly speed up note taking and reduce the administrative burden on practitioners during and after appointments.`
    ),
  ]),
  dependsOn: [],
  getRoute: (brand, _practice) => `settings/brand/${brand.ref.id}/snippets`,
  validate$: (practice$, step, options) => {
    return practice$.pipe(
      switchMap((practice) => {
        if (options?.reviewed || options?.skipped) {
          return from([{ valid: true } as IValidationResult]);
        }

        const brandRef = Firestore.getParentDocRef<IBrand>(practice.ref);
        return from(
          Firestore.getDocs(Brand.snippetCol({ ref: brandRef }))
        ).pipe(
          map((rawSnippets) => {
            const snippets = rawSnippets.filter(undeletedFilter);

            if (snippets.length === 0) {
              return { valid: true };
            }

            const pageVisitValidation =
              PracticeSetupStepValidation.validatePageVisits(
                step,
                pageVisitTracking,
                snippets.length
              );

            if (pageVisitValidation.valid) {
              return { valid: true };
            }

            const pageVisits = step?.pageVisits ?? [];
            const visitedSnippetIds = new Set(
              pageVisits
                .filter((visit) => visit.route.includes('snippets/'))
                .map((visit) => {
                  const segments = visit.route.split('/');
                  const snippetsIdx = segments.lastIndexOf('snippets');
                  return segments[snippetsIdx + 1];
                })
                .filter((id): id is string => !!id)
            );
            const remainingSnippets = snippets
              .filter((snippet) => !visitedSnippetIds.has(snippet.ref.id))
              .map((snippet) => snippet.name);

            return {
              valid: false,
              reason: `Please review and approve all snippets before completing this step (${
                pageVisitValidation.reason
              }). Remaining snippets: \r\n${remainingSnippets
                .map((snippet) => `- ${snippet}`)
                .sort()
                .join('\r\n')}`,
              progress: {
                current: pageVisitValidation.progress?.current || 0,
                total: snippets.length,
              },
            };
          })
        );
      })
    );
  },
  pageVisitTracking,
};
